import { BrowserCacheLocation, InteractionStatus, PublicClientApplication, type Configuration } from '@azure/msal-browser';
import { MsalProvider, useIsAuthenticated, useMsal } from '@azure/msal-react';
import { ArrowRight, ShieldCheck } from 'lucide-react';
import { useCallback, useEffect, useState, type ReactNode } from 'react';

const tenantSubdomain = import.meta.env.VITE_ENTRA_TENANT_SUBDOMAIN ?? 'trustemetrycustomers';
const tenantId = import.meta.env.VITE_ENTRA_TENANT_ID ?? 'a96f0f41-540c-4b80-8f98-a8950c0fd430';
const authorityHost = `${tenantSubdomain}.ciamlogin.com`;
const issuerHost = `${tenantId}.ciamlogin.com`;
const portalClientId = import.meta.env.VITE_ENTRA_CLIENT_ID ?? 'f7ec14a7-53a8-43a1-b356-303533bade09';
const apiClientId = import.meta.env.VITE_API_CLIENT_ID ?? '0125aa86-4f29-4eba-92c9-89bb6dad0bb0';
const apiBaseUrl = import.meta.env.VITE_API_BASE_URL ?? 'https://ca-trustemetry-dev-api.mangoocean-dfbd4ea9.centralus.azurecontainerapps.io';

const configuration: Configuration = {
  auth: {
    clientId: portalClientId,
    authority: `https://${authorityHost}/${tenantId}`,
    knownAuthorities: [authorityHost, issuerHost],
    redirectUri: `${window.location.origin}/auth/callback`,
    postLogoutRedirectUri: window.location.origin,
  },
  cache: { cacheLocation: BrowserCacheLocation.SessionStorage },
};

const loginRequest = {
  scopes: ['openid', 'profile', 'email', `api://${apiClientId}/access_as_user`],
};

export const customerIdentity = new PublicClientApplication(configuration);

export class CustomerApiError extends Error {
  constructor(public readonly status: number, public readonly payload: unknown) {
    super(`Trustemetry API returned ${status}.`);
  }
}

export type CustomerIdentity = {
  displayName: string;
  email: string;
  initials: string;
};

function claimString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

export function customerIdentityFromAccount(account: { name?: string; username?: string; idTokenClaims?: Record<string, unknown> } | undefined): CustomerIdentity {
  const claims = account?.idTokenClaims;
  const emailClaim = claimString(claims?.email);
  const emailList = Array.isArray(claims?.emails) ? claimString(claims.emails[0]) : undefined;
  const email = emailClaim ?? emailList ?? claimString(account?.username) ?? '';
  const displayName = claimString(claims?.name) ?? claimString(account?.name) ?? email.split('@')[0] ?? 'Customer';
  const nameParts = displayName.split(/\s+/).filter(Boolean);
  const initials = nameParts.slice(0, 2).map((part) => part[0]?.toUpperCase()).join('') || 'CU';
  return { displayName, email, initials };
}

export function useCustomerIdentity(): CustomerIdentity {
  const { accounts } = useMsal();
  return customerIdentityFromAccount(accounts[0]);
}

export function useCustomerAccountSwitch() {
  const { instance, accounts } = useMsal();
  return useCallback(async () => {
    await instance.logoutRedirect({
      account: accounts[0],
      postLogoutRedirectUri: window.location.href,
    });
  }, [accounts, instance]);
}

function SignInScreen() {
  const { instance } = useMsal();
  const [error, setError] = useState<string>();

  async function signIn() {
    setError(undefined);
    try {
      await instance.loginRedirect(loginRequest);
    } catch (cause) {
      const detail = cause instanceof Error ? cause.message : 'Unknown authentication error';
      console.error('Customer sign-in failed', cause);
      setError(`We could not start sign-in. ${detail}`);
    }
  }

  return (
    <main className="sign-in-page">
      <section className="sign-in-card" aria-labelledby="sign-in-title">
        <img src="/trustemetry-logo.png" alt="Trustemetry — Domain Trust Observability" />
        <div className="sign-in-icon"><ShieldCheck size={24} aria-hidden="true" /></div>
        <p className="eyebrow">Customer portal</p>
        <h1 id="sign-in-title">See how the security ecosystem trusts your domains.</h1>
        <p>Sign in with your organization email to access monitored domains, classification changes, and ecosystem consensus.</p>
        <button className="primary-button sign-in-button" onClick={signIn}>Sign in or create an account <ArrowRight size={17} /></button>
        {error && <p className="sign-in-error" role="alert">{error}</p>}
        <small>Early-access environment · Customer identities are kept separate from Trustemetry administration.</small>
      </section>
    </main>
  );
}

function AuthenticationBoundary({ children }: { children: ReactNode }) {
  const authenticated = useIsAuthenticated();
  const { inProgress } = useMsal();

  if (inProgress !== InteractionStatus.None) return <main className="sign-in-page"><p className="auth-loading" role="status">Preparing secure sign-in…</p></main>;
  return authenticated ? children : <SignInScreen />;
}

function InvitationActivation({ children }: { children: ReactNode }) {
  const { instance, accounts } = useMsal();
  const token = new URLSearchParams(window.location.search).get('token');
  const [state, setState] = useState<'working' | 'failed' | 'done'>(token ? 'working' : 'done');
  useEffect(() => {
    if (!token || state !== 'working' || !accounts[0]) return;
    void instance.acquireTokenSilent({ account: accounts[0], scopes: [`api://${apiClientId}/access_as_user`] }).then(async (result) => {
      const response = await fetch(`${apiBaseUrl}/v1/team/invitations/accept`, {
        method: 'POST', headers: { Authorization: `Bearer ${result.accessToken}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ token })
      });
      if (!response.ok) throw new Error('Invitation could not be accepted.');
      window.history.replaceState({}, '', '/overview');
      setState('done');
    }).catch(() => setState('failed'));
  }, [accounts, instance, state, token]);
  if (state === 'working') return <main className="sign-in-page"><section className="sign-in-card"><p className="eyebrow">Team invitation</p><h1>Activating your access…</h1><p className="auth-loading" role="status">We’re securely connecting your account to the invited organization.</p></section></main>;
  if (state === 'failed') return <main className="sign-in-page"><section className="sign-in-card"><p className="eyebrow">Team invitation</p><h1>We couldn’t activate this invitation.</h1><p className="sign-in-error" role="alert">The link may have expired, already been used, or it may not match the email address you used to sign in. Ask your organization administrator to send a new invitation.</p></section></main>;
  return children;
}

export function CustomerAuthentication({ children }: { children: ReactNode }) {
  return <MsalProvider instance={customerIdentity}><AuthenticationBoundary><InvitationActivation>{children}</InvitationActivation></AuthenticationBoundary></MsalProvider>;
}

export function useCustomerApi() {
  const { instance, accounts } = useMsal();
  return useCallback(async <T,>(path: string, init?: RequestInit): Promise<T> => {
    const account = accounts[0];
    if (!account) throw new Error('Customer account is not signed in.');
    const token = await instance.acquireTokenSilent({ account, scopes: [`api://${apiClientId}/access_as_user`] });
    const headers = new Headers(init?.headers);
    headers.set('Authorization', `Bearer ${token.accessToken}`);
    if (init?.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
    const response = await fetch(`${apiBaseUrl}${path}`, {
      ...init,
      headers,
    });
    if (!response.ok) {
      let payload: unknown;
      try { payload = await response.json(); } catch { payload = undefined; }
      throw new CustomerApiError(response.status, payload);
    }
    return response.json() as Promise<T>;
  }, [accounts, instance]);
}
